﻿import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { toast } from 'sonner';
import { z } from 'zod';
import { Plus, Pencil, Trash2, MapPin } from 'lucide-react';
import { format } from 'date-fns';
import { PERMISSIONS } from '@pms/shared';
import { createDmShootActivitySchema } from '@pms/shared';
import type { CreateDmShootActivityInput, UpdateDmShootActivityInput } from '@pms/shared';
import { dmTeamService } from '@/services/dm-team.service';
import type { DmShootActivity, DmUserRef } from '@/services/dm-team.service';
import { usePermission } from '@/hooks/usePermission';
import { DataTable } from '@/components/ui/data-table';
import type { Column } from '@/components/ui/data-table';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from '@/components/ui/dialog';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel,
  AlertDialogContent, AlertDialogDescription, AlertDialogFooter,
  AlertDialogHeader, AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import { DateTimePicker } from '@/components/ui/date-time-picker';
import { DateRangePicker } from '@/components/ui/date-range-picker';

// ── Shoot Form ────────────────────────────────────────────────────────────────

const shootFormSchema = createDmShootActivitySchema.extend({
  date_time: z.string().min(1, 'Date & time is required'),
  location:  z.string().trim().min(1, 'Location is required'),
});
type ShootFormData = z.infer<typeof shootFormSchema>;

function localToISO(v: string): string {
  if (!v) return v;
  return new Date(v.length === 16 ? v + ':00' : v).toISOString();
}

function ShootForm({
  defaultValues,
  shootTeam,
  assignList,
  onSubmit,
  pending,
  onCancel,
}: {
  defaultValues?: Partial<ShootFormData>;
  shootTeam: DmUserRef[];
  assignList: DmUserRef[];
  onSubmit: (data: CreateDmShootActivityInput) => void;
  pending: boolean;
  onCancel: () => void;
}) {
  const form = useForm<ShootFormData>({
    resolver: zodResolver(shootFormSchema),
    defaultValues: defaultValues ?? {},
  });

  return (
    <form
      onSubmit={form.handleSubmit(d =>
        onSubmit({ ...d, date_time: localToISO(d.date_time) } as CreateDmShootActivityInput)
      )}
      className="space-y-4"
    >
      <div>
        <Label>Title *</Label>
        <Input {...form.register('title')} className="mt-1" placeholder="Shoot title…" />
        {form.formState.errors.title && (
          <p className="text-xs text-destructive mt-1">{form.formState.errors.title.message}</p>
        )}
      </div>
      <div>
        <Label>Date & Time *</Label>
        <div className="mt-1">
          <DateTimePicker
            value={form.watch('date_time') || null}
            onChange={v => form.setValue('date_time', v || '', { shouldValidate: true })}
          />
        </div>
        {form.formState.errors.date_time && (
          <p className="text-xs text-destructive mt-1">{form.formState.errors.date_time.message}</p>
        )}
      </div>
      <div className="grid grid-cols-2 gap-3">
        <div>
          <Label>Who Shot</Label>
          <Select
            defaultValue={defaultValues?.who_shot_id ?? ''}
            onValueChange={v => form.setValue('who_shot_id', v)}
          >
            <SelectTrigger className="mt-1"><SelectValue placeholder="Select…" /></SelectTrigger>
            <SelectContent>
              {shootTeam.map(u => (
                <SelectItem key={u.id} value={u.id}>{u.first_name} {u.last_name}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label>Assigned</Label>
          <Select
            defaultValue={defaultValues?.assigned_id ?? ''}
            onValueChange={v => form.setValue('assigned_id', v)}
          >
            <SelectTrigger className="mt-1"><SelectValue placeholder="Select…" /></SelectTrigger>
            <SelectContent>
              {assignList.map(u => (
                <SelectItem key={u.id} value={u.id}>{u.first_name} {u.last_name}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>
      <div>
        <Label>Location *</Label>
        <Input {...form.register('location')} className="mt-1" placeholder="Location / NAS path…" />
        {form.formState.errors.location && (
          <p className="text-xs text-destructive mt-1">{form.formState.errors.location.message}</p>
        )}
      </div>
      <DialogFooter>
        <Button variant="outline" type="button" onClick={onCancel}>Cancel</Button>
        <Button type="submit" disabled={pending || !form.watch('location')?.trim()}>Save</Button>
      </DialogFooter>
    </form>
  );
}

// ── Mobile Card ───────────────────────────────────────────────────────────────

function MobileShootCard({ row, canEdit, canDelete, onEdit, onDelete }: {
  row: DmShootActivity;
  canEdit: boolean;
  canDelete: boolean;
  onEdit: (row: DmShootActivity) => void;
  onDelete: (id: string) => void;
}) {
  return (
    <div className={`rounded-lg border p-3 space-y-1${row.location ? ' bg-purple-50/60 dark:bg-purple-900/20' : ''}`}>
      <div className="flex items-start justify-between gap-2">
        <p className="font-medium truncate flex-1">{row.title}</p>
        <div className="flex gap-1 shrink-0">
          {canEdit && row.source === 'shoot' && (
            <Button size="icon" variant="ghost" className="h-8 w-8 sm:h-8 sm:w-8" onClick={() => onEdit(row)}>
              <Pencil className="h-4 w-4" />
            </Button>
          )}
          {canDelete && row.source === 'shoot' && (
            <Button size="icon" variant="ghost" className="h-8 w-8 sm:h-8 sm:w-8 text-destructive hover:text-destructive" onClick={() => onDelete(row.id)}>
              <Trash2 className="h-4 w-4" />
            </Button>
          )}
          {row.source !== 'shoot' && (
            <div className="flex items-center gap-1.5">
              {row.status && (
                <span className={`text-xs px-1.5 py-0.5 rounded-full font-medium ${
                  row.status === 'completed'   ? 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400' :
                  row.status === 'in_progress' ? 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400' :
                  row.status === 'cancelled'   ? 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400' :
                                                 'bg-gray-100 text-gray-600 dark:bg-gray-800 dark:text-gray-400'
                }`}>{row.status.replace('_', ' ')}</span>
              )}
              <span className="text-xs text-muted-foreground italic self-center">Via Activities</span>
            </div>
          )}
        </div>
      </div>
      <p className="text-xs text-muted-foreground">
        {format(new Date(row.date_time), 'd MMM yyyy, HH:mm')}
      </p>
      {row.location && (
        <p className="text-xs text-muted-foreground flex items-center gap-1">
          <MapPin className="h-3 w-3" />{row.location}
        </p>
      )}
      {row.source === 'shoot' && (row.who_shot || row.assigned) && (
        <p className="text-xs text-muted-foreground">
          {row.who_shot && <>Shot: {row.who_shot.first_name} {row.who_shot.last_name}</>}
          {row.who_shot && row.assigned && ' · '}
          {row.assigned && <>Assigned: {row.assigned.first_name} {row.assigned.last_name}</>}
        </p>
      )}
      {row.source === 'activity' && (row.assigned_users ?? []).length > 0 && (
        <p className="text-xs text-muted-foreground">
          Assigned: {row.assigned_users!.map(u => `${u.first_name} ${u.last_name}`).join(', ')}
        </p>
      )}
    </div>
  );
}

// ── Main Page ─────────────────────────────────────────────────────────────────

export function ShootsPage() {
  const qc = useQueryClient();
  const permission = usePermission();
  const canEdit = permission.has(PERMISSIONS.DM_TEAM_SHOOTS_EDIT);
  const canDelete = permission.has(PERMISSIONS.DM_TEAM_SHOOTS_DELETE);

  const [search, setSearch] = useState('');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [createOpen, setCreateOpen] = useState(false);
  const [editItem, setEditItem] = useState<DmShootActivity | null>(null);
  const [deleteId, setDeleteId] = useState<string | null>(null);
  const [page, setPage] = useState(1);

  const { data: metaData } = useQuery({
    queryKey: ['dm-team', 'shoots-meta'],
    queryFn: () => dmTeamService.getShootMeta(),
  });

  const { data, isLoading, isError, refetch } = useQuery({
    queryKey: ['dm-team', 'shoots', { page, q: search, dateFrom, dateTo }],
    queryFn: () => dmTeamService.listShootActivities({
      page, limit: 20,
      q: search || undefined,
      date_from: dateFrom || undefined,
      date_to: dateTo || undefined,
    }),
  });

  const createMutation = useMutation({
    mutationFn: (body: CreateDmShootActivityInput) => dmTeamService.createShootActivity(body),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['dm-team', 'shoots'] });
      setCreateOpen(false);
      toast.success('Shoot activity created');
    },
    onError: (e: any) => toast.error(e?.response?.data?.error?.message ?? 'Failed to create'),
  });

  const editMutation = useMutation({
    mutationFn: ({ id, body }: { id: string; body: UpdateDmShootActivityInput }) =>
      dmTeamService.updateShootActivity(id, body),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['dm-team', 'shoots'] });
      setEditItem(null);
      toast.success('Shoot activity updated');
    },
    onError: () => toast.error('Failed to update'),
  });

  const deleteMutation = useMutation({
    mutationFn: (id: string) => dmTeamService.deleteShootActivity(id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['dm-team', 'shoots'] });
      setDeleteId(null);
      toast.success('Deleted');
    },
    onError: () => toast.error('Failed to delete'),
  });

  const shootTeam = metaData?.data?.shootTeam ?? [];
  const assignList = metaData?.data?.assignList ?? [];
  const canCreate = metaData?.data?.hasCreateAccess ?? false;
  const pagination = data?.pagination;

  const COLUMNS: Column<DmShootActivity>[] = [
    {
      key: 'date_time',
      header: 'Date',
      cell: row => (
        <span className="text-sm whitespace-nowrap">
          {format(new Date(row.date_time), 'd MMM yyyy, HH:mm')}
        </span>
      ),
      className: 'whitespace-nowrap',
    },
    {
      key: 'title',
      header: 'Title',
      cell: row => <span className="font-medium">{row.title}</span>,
    },
    {
      key: 'who_shot',
      header: 'Who Shot',
      cell: row => {
        if (row.source === 'activity') {
          return row.category
            ? <span className="text-xs text-muted-foreground italic">{row.category}</span>
            : <span className="text-muted-foreground">—</span>;
        }
        return row.who_shot
          ? <span className="text-sm">{row.who_shot.first_name} {row.who_shot.last_name}</span>
          : <span className="text-muted-foreground">—</span>;
      },
    },
    {
      key: 'assigned',
      header: 'Assigned',
      cell: row => {
        if (row.source === 'activity') {
          const users = row.assigned_users ?? [];
          if (users.length === 0) return <span className="text-muted-foreground">—</span>;
          const first = `${users[0]!.first_name} ${users[0]!.last_name}`;
          return (
            <span className="text-sm">
              {first}{users.length > 1 && <span className="text-muted-foreground ml-1">+{users.length - 1}</span>}
            </span>
          );
        }
        return row.assigned
          ? <span className="text-sm">{row.assigned.first_name} {row.assigned.last_name}</span>
          : <span className="text-muted-foreground">—</span>;
      },
    },
    {
      key: 'location',
      header: 'Location',
      cell: row => row.location ? (
        <span className="flex items-center gap-1 text-sm">
          <MapPin className="w-3 h-3 text-purple-500 shrink-0" />
          <span className="truncate max-w-[180px]">{row.location}</span>
        </span>
      ) : <span className="text-muted-foreground">—</span>,
    },
    {
      key: 'actions',
      header: '',
      cell: row => row.source === 'shoot' ? (
        <div className="flex gap-1">
          {canEdit && (
            <Button
              variant="ghost" size="icon" className="h-8 w-8 sm:h-8 sm:w-8"
              onClick={() => setEditItem(row)}
            >
              <Pencil className="h-4 w-4" />
            </Button>
          )}
          {canDelete && (
            <Button
              variant="ghost" size="icon" className="h-8 w-8 sm:h-8 sm:w-8 text-destructive hover:text-destructive"
              onClick={() => setDeleteId(row.id)}
            >
              <Trash2 className="h-4 w-4" />
            </Button>
          )}
        </div>
      ) : (
        <div className="flex items-center gap-2 flex-nowrap">
          {row.status && (
            <span className={`shrink-0 text-xs px-2 py-0.5 rounded-full font-medium whitespace-nowrap ${
              row.status === 'completed'   ? 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400' :
              row.status === 'in_progress' ? 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400' :
              row.status === 'cancelled'   ? 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400' :
                                             'bg-gray-100 text-gray-600 dark:bg-gray-800 dark:text-gray-400'
            }`}>{row.status.replace('_', ' ')}</span>
          )}
          <span className="shrink-0 text-xs text-muted-foreground italic whitespace-nowrap">Via Activities</span>
        </div>
      ),
      className: 'w-56 whitespace-nowrap',
    },
  ];

  return (
    <div className="space-y-4">
      <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="text-2xl font-bold tracking-tight">All Shoot List</h1>
          <p className="text-sm text-muted-foreground mt-0.5">Photo and video shoot activities</p>
        </div>
        {canCreate && (
          <Button className="self-start sm:self-auto" onClick={() => setCreateOpen(true)}>
            <Plus className="w-4 h-4 mr-1" /> Add Shoot
          </Button>
        )}
      </div>

      <div className="flex flex-col gap-2 sm:flex-row sm:flex-wrap sm:items-center">
        <Input
          value={search}
          onChange={e => { setSearch(e.target.value); setPage(1); }}
          placeholder="Search by title or location…"
          className="w-full sm:max-w-xs"
        />
        <DateRangePicker
          from={dateFrom}
          to={dateTo}
          onChange={(f, t) => { setDateFrom(f); setDateTo(t); setPage(1); }}
          placeholder="Filter by date range"
          className="w-full sm:w-56"
        />
      </div>

      <div className="hidden md:block">
        <DataTable
          columns={COLUMNS}
          rows={data?.data ?? []}
          rowKey={r => r.id}
          rowClassName={r => r.location ? 'bg-purple-50/60 dark:bg-purple-900/20' : undefined}
          loading={isLoading}
          error={isError}
          onRetry={refetch}
          empty={
            <div className="py-8 text-center">
              <p className="text-sm text-muted-foreground">No shoot activities found</p>
              {canCreate && (
                <Button variant="outline" size="sm" className="mt-2" onClick={() => setCreateOpen(true)}>
                  <Plus className="w-3.5 h-3.5 mr-1" /> Add first shoot
                </Button>
              )}
            </div>
          }
          pagination={pagination ? {
            page: pagination.page,
            totalPages: pagination.totalPages,
            total: pagination.total,
            onPageChange: setPage,
          } : undefined}
        />
      </div>
      <div className="block md:hidden space-y-2">
        {isLoading ? (
          <div className="py-8 text-center text-sm text-muted-foreground">Loading…</div>
        ) : (data?.data ?? []).length === 0 ? (
          <div className="py-8 text-center">
            <p className="text-sm text-muted-foreground">No shoot activities found</p>
          </div>
        ) : (data?.data ?? []).map(row => (
          <MobileShootCard key={row.id} row={row} canEdit={canEdit} canDelete={canDelete}
            onEdit={setEditItem} onDelete={setDeleteId} />
        ))}
      </div>

      {/* Create modal */}
      <Dialog open={createOpen} onOpenChange={setCreateOpen}>
        <DialogContent>
          <DialogHeader><DialogTitle>Add Shoot Activity</DialogTitle></DialogHeader>
          <ShootForm
            shootTeam={shootTeam}
            assignList={assignList}
            onSubmit={data => createMutation.mutate(data)}
            pending={createMutation.isPending}
            onCancel={() => setCreateOpen(false)}
          />
        </DialogContent>
      </Dialog>

      {/* Edit modal */}
      <Dialog open={!!editItem} onOpenChange={v => !v && setEditItem(null)}>
        <DialogContent>
          <DialogHeader><DialogTitle>Edit Shoot Activity</DialogTitle></DialogHeader>
          {editItem && (
            <ShootForm
              key={editItem.id}
              defaultValues={{
                title: editItem.title,
                location: editItem.location ?? '',
                date_time: editItem.date_time ? format(new Date(editItem.date_time), "yyyy-MM-dd'T'HH:mm") : '',
                who_shot_id: editItem.who_shot?.id ?? '',
                assigned_id: editItem.assigned?.id ?? '',
              }}
              shootTeam={shootTeam}
              assignList={assignList}
              onSubmit={body => editMutation.mutate({ id: editItem.id, body })}
              pending={editMutation.isPending}
              onCancel={() => setEditItem(null)}
            />
          )}
        </DialogContent>
      </Dialog>

      <AlertDialog open={!!deleteId} onOpenChange={v => !v && setDeleteId(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete shoot activity?</AlertDialogTitle>
            <AlertDialogDescription>This action cannot be undone.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => deleteId && deleteMutation.mutate(deleteId)}
              className="bg-destructive text-destructive-foreground"
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
